/**
 * Formação — os módulos que o agente dá ao trabalhador, um a um.
 *
 * Cada módulo traz o resumo do que tem de ser dito, para que dois agentes
 * diferentes ensinem a mesma coisa. Marcar o último obrigatório passa o
 * trabalhador a "Formação concluída" sozinho: não há um botão à parte para
 * alguém se esquecer de carregar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2, Circle, GraduationCap } from 'lucide-react';
import api from '../../utils/api';

const FieldTraining = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState(null);
  const [erro, setErro] = useState('');
  const [aMarcar, setAMarcar] = useState(null);

  const carregar = useCallback(() => {
    api.get(`/operations/workers/${id}/training`)
      .then((r) => setEstado(r.data))
      .catch((e) => setErro(e.response?.data?.message || 'Não foi possível carregar a formação.'));
  }, [id]);
  useEffect(() => { carregar(); }, [carregar]);

  const concluir = async (codigo) => {
    setAMarcar(codigo); setErro('');
    try {
      const { data } = await api.post(`/operations/workers/${id}/training/${codigo}`);
      setEstado(data);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível registar o módulo.');
    } finally { setAMarcar(null); }
  };

  if (!estado) {
    return (
      <div className="flex justify-center py-16">
        {erro ? <p className="text-red-400 text-sm text-center px-6">{erro}</p>
              : <Loader2 className="w-6 h-6 animate-spin text-slate-600" />}
      </div>
    );
  }

  const pct = estado.obrigatorios ? Math.round((estado.concluidos / estado.obrigatorios) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} aria-label="Voltar"
          className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-slate-400">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold leading-tight">Formação</h1>
          <p className="text-xs text-slate-500">
            {estado.concluidos} de {estado.obrigatorios} módulos obrigatórios
          </p>
        </div>
      </div>

      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>

      {estado.completo && (
        <div className="flex gap-2 p-3 rounded-xl bg-green-500/10 text-green-300 text-sm">
          <GraduationCap className="w-5 h-5 shrink-0" />
          <span>Formação completa. O profissional está pronto para a activação pela gestão.</span>
        </div>
      )}

      {erro && <p className="text-sm text-red-400">{erro}</p>}

      <ul className="space-y-2">
        {estado.modulos.map((m) => {
          const feito = !!m.concluido_em;
          return (
            <li key={m.codigo}
              className={`p-3 rounded-xl border ${feito ? 'border-green-500/30 bg-green-500/5' : 'border-slate-800 bg-slate-900'}`}>
              <div className="flex items-start gap-3">
                {feito
                  ? <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                  : <Circle className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100">
                    {m.titulo}
                    {!m.obrigatorio && <span className="ml-2 text-[11px] font-normal text-slate-500">opcional</span>}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{m.resumo}</p>
                  {feito && (
                    <p className="text-xs text-slate-600 mt-1.5">
                      {new Date(m.concluido_em).toLocaleDateString('pt-PT')}
                      {m.formador && ` · ${m.formador}`}
                    </p>
                  )}
                </div>
              </div>

              {!feito && (
                <button onClick={() => concluir(m.codigo)} disabled={aMarcar === m.codigo}
                  className="w-full mt-3 min-h-[44px] rounded-lg bg-slate-800 text-slate-200 font-medium
                             inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  {aMarcar === m.codigo && <Loader2 className="w-4 h-4 animate-spin" />}
                  Dei este módulo
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FieldTraining;
